import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2 } from 'lucide-react';
import { getProcedureTemplate } from '../constants/procedureTemplates';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditProcedureModal({ isOpen, onClose, procedure, onUpdate, token }) {
    const [loading, setLoading] = useState(false);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        procedure_number: '',
        authority: '',
        client_id: '',
        responsible_user_id: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        filing_date: '',
        deadline_date: '',
        resolution_date: '',
        metadata: {}
    });

    useEffect(() => {
        if (isOpen && token) {
            fetchData();
        }
    }, [isOpen, token]);

    const fetchData = async () => {
        try {
            // Calculate promises
            const promises = [axios.get(`${API}/clients`, { 
                headers: { Authorization: `Bearer ${token}` },
                params: { limit: 1000 }
            })];

            // Attempt to fetch users - might fail if not admin, so we handle it gracefully
            const usersPromise = axios.get(`${API}/organization/users`, { headers: { Authorization: `Bearer ${token}` } })
                .catch(err => {
                    console.warn("Could not fetch users (likely permission issue)", err);
                    return { data: [] };
                });

            promises.push(usersPromise);

            const [clientsRes, usersRes] = await Promise.all(promises);
            const clientsData = (clientsRes.data && clientsRes.data.items) ? clientsRes.data.items : (Array.isArray(clientsRes.data) ? clientsRes.data : []);
            setClients(clientsData);
            setUsers(usersRes.data);

        } catch (error) {
            console.error("Error fetching dependencies:", error);
            // Don't block editing if fetching lists fails
        }
    };

    useEffect(() => {
        if (procedure) {
            setFormData({
                title: procedure.title || '',
                procedure_number: procedure.procedure_number || '',
                authority: procedure.authority || 'IMPI',
                client_id: procedure.client_id || '',
                responsible_user_id: procedure.responsible_user_id || '',
                description: procedure.description || '',
                status: procedure.status || 'pending',
                priority: procedure.priority || 'medium',
                filing_date: procedure.filing_date ? procedure.filing_date.split('T')[0] : '',
                deadline_date: procedure.deadline_date ? procedure.deadline_date.split('T')[0] : '',
                resolution_date: procedure.resolution_date ? procedure.resolution_date.split('T')[0] : '',
                metadata: procedure.metadata || {}
            });
        }
    }, [procedure]);

    const template = procedure ? getProcedureTemplate(procedure.procedure_type) : null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleMetadataChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            metadata: {
                ...prev.metadata,
                [key]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                filing_date: formData.filing_date ? new Date(formData.filing_date).toISOString() : null,
                deadline_date: formData.deadline_date ? new Date(formData.deadline_date).toISOString() : null,
                resolution_date: formData.resolution_date ? new Date(formData.resolution_date).toISOString() : null,
            };
            await onUpdate(payload);
        } catch (error) {
            console.error("Error in modal submit", error);
            toast.error("Error al guardar cambios");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar Procedimiento</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6 py-4">
                    {/* Standard Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2 space-y-2">
                            <Label htmlFor="title">Título</Label>
                            <Input
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="procedure_number">Número de Expediente</Label>
                            <Input
                                id="procedure_number"
                                name="procedure_number"
                                value={formData.procedure_number}
                                onChange={handleChange}
                                placeholder="Ej. 12345/2024"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="authority">Autoridad</Label>
                            <Select
                                value={formData.authority}
                                onValueChange={(val) => handleSelectChange('authority', val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="IMPI">IMPI</SelectItem>
                                    <SelectItem value="TFJA">TFJA</SelectItem>
                                    <SelectItem value="COURT">Juzgados de Distrito / Colegiados</SelectItem>
                                    <SelectItem value="OTHER">Otro</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="client_id">Cliente</Label>
                            <Select
                                value={formData.client_id}
                                onValueChange={(val) => handleSelectChange('client_id', val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar Cliente" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map(client => (
                                        <SelectItem key={client.id} value={client.id}>
                                            {client.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="responsible_user_id">Responsable</Label>
                            <Select
                                value={formData.responsible_user_id}
                                onValueChange={(val) => handleSelectChange('responsible_user_id', val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar Responsable" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                                    {users.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            {user.nombre}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="md:col-span-2 space-y-2">
                            <Label htmlFor="description">Descripción</Label>
                            <Textarea
                                id="description"
                                name="description"
                                rows={3}
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="status">Estado</Label>
                            <Select
                                value={formData.status}
                                onValueChange={(val) => handleSelectChange('status', val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Borrador</SelectItem>
                                    <SelectItem value="open">Abierto</SelectItem>
                                    <SelectItem value="active">Activo</SelectItem>
                                    <SelectItem value="waiting_client">Esperando Cliente</SelectItem>
                                    <SelectItem value="waiting_authority">Esperando Autoridad</SelectItem>
                                    <SelectItem value="required_action">Acción Requerida</SelectItem>
                                    <SelectItem value="resolved">Resuelto</SelectItem>
                                    <SelectItem value="closed">Cerrado</SelectItem>
                                    <SelectItem value="suspended">Suspendido</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="priority">Prioridad</Label>
                            <Select
                                value={formData.priority}
                                onValueChange={(val) => handleSelectChange('priority', val)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Prioridad" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Baja</SelectItem>
                                    <SelectItem value="medium">Media</SelectItem>
                                    <SelectItem value="high">Alta</SelectItem>
                                    <SelectItem value="urgent">Urgente</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="filing_date">Fecha de Presentación</Label>
                            <Input
                                id="filing_date"
                                name="filing_date"
                                type="date"
                                value={formData.filing_date}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="deadline_date">Fecha Límite</Label>
                            <Input
                                id="deadline_date"
                                name="deadline_date"
                                type="date"
                                value={formData.deadline_date}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="resolution_date">Fecha de Resolución</Label>
                            <Input
                                id="resolution_date"
                                name="resolution_date"
                                type="date"
                                value={formData.resolution_date}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    {/* Dynamic Metadata Fields */}
                    {template && template.fields && template.fields.length > 0 && (
                        <div className="pt-4 border-t border-gray-100">
                            <h4 className="text-sm font-medium text-gray-900 mb-4">Información Adicional ({template.label})</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {template.fields.map((field) => (
                                    <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2 space-y-2' : 'space-y-2'}>
                                        <Label htmlFor={`meta-${field.key}`}>{field.label}</Label>
                                        {field.type === 'textarea' ? (
                                            <Textarea
                                                id={`meta-${field.key}`}
                                                value={formData.metadata[field.key] || ''}
                                                onChange={(e) => handleMetadataChange(field.key, e.target.value)}
                                                rows={3}
                                            />
                                        ) : field.type === 'date' ? (
                                            <Input
                                                id={`meta-${field.key}`}
                                                type="date"
                                                value={formData.metadata[field.key] || ''}
                                                onChange={(e) => handleMetadataChange(field.key, e.target.value)}
                                            />
                                        ) : (
                                            <Input
                                                id={`meta-${field.key}`}
                                                type="text"
                                                value={formData.metadata[field.key] || ''}
                                                onChange={(e) => handleMetadataChange(field.key, e.target.value)}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Guardar Cambios
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
